import React, { useEffect, useState, useRef } from "react";
import { Transaction } from "../types";
import { hexToArabic } from "../util";
import {
  AmountRiyal,
  convertToArabicNumerals,
  handleSwitchResponse,
  providerStatusCode,
  isHexEncoded,
} from "../util";
import { Typography, Button } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import Mada from "../assets/mada.svg?react";
import { formatCardExpiration } from "../utils/formatCardExpiration";
import AcquirerBrand from "./AcquirerBrand";

const ReceiptPage: React.FC = () => {
  const [receipt, setReceipt] = useState<Transaction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const receiptRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const qrData = params.get("data");
        if (!qrData) {
          setError("Data not found in URL");
          return;
        }
        // 1️⃣ Decode Base64 (URL-safe → standard)
        const base64 = qrData.replace(/-/g, "+").replace(/_/g, "/");
        const binary = atob(base64);

        // 2️⃣ Convert binary to Uint8Array
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
          bytes[i] = binary.charCodeAt(i);
        }

        // 3️⃣ Decompress GZIP using browser DecompressionStream
        const blob = new Blob([bytes], { type: "application/gzip" });
        const stream = blob
          .stream()
          .pipeThrough(new DecompressionStream("gzip"));
        const jsonText = await new Response(stream).text();

        // 4️⃣ Parse JSON to Transaction object
        const parsed: Transaction = JSON.parse(jsonText);
        setReceipt(parsed);
      } catch (err) {
        console.error(err);
        setError("Failed to decode or parse QRData");
      }
    })();
  }, []);

  const receiptRefFn = useReactToPrint({
    contentRef: receiptRef,
    documentTitle: receipt?.rrn,
  });

  if (error) {
    return <div className="text-center mt-10 text-red-500">{error}</div>;
  }
  if (!receipt) {
    return <div className="text-center mt-10">Loading receipt...</div>;
  }
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-6">
     <Mada className="w-20 h-20" />

      <div className="w-full max-w-md relative">
        <div className="absolute top-0 right-0 p-2">
          <Button
            type="default"
            icon={<PrinterOutlined />}
            onClick={() => {
              receiptRefFn();
            }}
          />
        </div>

        <div className="justify-center print:flex" ref={receiptRef}>
          <div
            className="p-6 rounded-lg shadow-lg max-w-md w-full  leading-5 text-[.82rem] font-bold"
            //  className="fixed inset-0 bg-gray-900 bg-opacity-50   items-top justify-center hidden print:flex "
          >
            <h1 className="text-center">{isHexEncoded(receipt?.merchantAr) ? hexToArabic(receipt.merchantAr) : receipt?.merchantAr}</h1>
            <h1 className="text-center">{receipt?.merchantEn}</h1>
            <h1 className="text-center">{isHexEncoded(receipt?.addAr) ? hexToArabic(receipt.addAr) : receipt?.addAr}</h1>
            <h1 className="text-center">{receipt?.addEn}</h1>

            <div className="text-[.6rem] font-normal leading-5">
              <div className="flex justify-between">
              <h1>{receipt?.startDate}</h1>
              <h1>{receipt?.startTime}</h1>
              
            
              
              
              </div>

              <div className="flex justify-between">
                <h1><AcquirerBrand code={receipt?.acquirerCode} showLogo={false} /></h1>
                <h1>{receipt?.merchantId}</h1>
                <h1>{receipt?.terminalId}</h1>
              </div>
              <div className="flex justify-between">
                <h1>{receipt?.merchantCategoryCode}</h1>
                <h1>{receipt?.stan}</h1>
                <h1>{receipt?.madaAppVersion}</h1>
                <h1>{receipt?.rrn}</h1>
              </div>
            </div>

            <div className="flex justify-between mt-2">
              <h1>{receipt?.cardSchemeEn}</h1>
              <h1 className="text-end">{receipt?.cardSchemeAr}</h1>
            </div>
            <div className="flex justify-between mt-2">
              <h1>{receipt?.transactionTypeEn}</h1>
              <h1 className="text-end">{receipt?.transactionTypeAr}</h1>
            </div>

            <div className="flex justify-between mt-2">
              <h1>{receipt?.pan}</h1>
              <h1>{formatCardExpiration(receipt?.cardExpiration)}</h1>
            </div>

            <div className="flex justify-between mt-2">
              <h1>
                {receipt.status == "APPROVED" ? (
                  receipt.amountAuthorizedValue &&
                  AmountRiyal(
                    convertToArabicNumerals(parseFloat(receipt.amountAuthorizedValue).toFixed(2)),
                    false,
                    false,
                    true,
                    true
                  )
                ) : (
                  <Typography className="flex flex-row-reverse">
                    {receipt.amount &&
                      AmountRiyal(
                        convertToArabicNumerals((receipt.amount / 100).toFixed(2)),
                        false,
                        false,
                        false,
                        true
                      )}
                  </Typography>
                )}
              </h1>
              <h1>{receipt.amountAuthorizedAr}</h1>
            </div>

            <div className="flex justify-between mt-2">
              <h1>{receipt.amountAuthorizedEn}</h1>
              <h1>
                {receipt.status == "APPROVED" ? (
                  <>
                    {AmountRiyal(
                      receipt.amountAuthorizedValue,
                      false,
                      false,
                      false,
                      true
                    )}
                  </>
                ) : (
                  <>
                    <Typography className="flex flex-row-reverse">
                      {receipt.amount && AmountRiyal(receipt.amount)}
                    </Typography>
                  </>
                )}
              </h1>
            </div>

            <div className="flex flex-col  mt-2 text-center">
              {}

                <h1 className="text-center">{receipt?.statusMessageAr}</h1>
              <h1 className="text-center">{receipt?.statusMessageEn}</h1>
          

<h1 className="text-center break-all">
                {receipt?.verificationMethodAr && /^[0-9A-Fa-f]+$/.test(receipt.verificationMethodAr) 
                  ? hexToArabic(receipt.verificationMethodAr) 
                  : receipt?.verificationMethodAr}
              </h1>
              <h1 className="text-center break-all">
                {receipt?.verificationMethodEn && /^[0-9A-Fa-f]+$/.test(receipt.verificationMethodEn) 
                  ? hexToArabic(receipt.verificationMethodEn) 
                  : receipt?.verificationMethodEn}
              </h1>
             
              </div>

            {receipt?.status === "DECLINED" && (
              <div className="text-center">
                {/* {handleSwitchResponse(receipt?.switchResponseCode).description} */}

                {receipt?.switchResponseCode == null
                  ? providerStatusCode(receipt?.providerResponseCode)
                  : handleSwitchResponse(receipt?.switchResponseCode)
                      ?.description}
              </div>
            )}

            <div
              style={{ display: receipt?.approvalCodeValue ? "block" : "none" }}
            >
              <div className="flex justify-between mt-2">
                <h1>
                  {receipt?.approvalCodeValue ? convertToArabicNumerals(receipt.approvalCodeValue) : ""}
                </h1>
                <h1>{receipt?.approvalCodeAr}</h1>
              </div>

              <div className="flex justify-between mt-2">
                <h1>{receipt?.approvalCodeEn}</h1>
                <h1>{receipt?.approvalCodeValue}</h1>
              </div>
            </div>

            <div className="flex justify-between mt-2">
              <h1>{receipt?.startDate}</h1>
              <h1>{receipt?.startTime}</h1>

            </div>

            <div className="flex flex-col  mt-4">
              <h1 className="text-center">{receipt?.thanksMessageAr}</h1>
              <h1 className="text-center">{receipt?.thanksMessageEn}</h1>
              <h1 className="text-center">{receipt?.saveReceiptMessageAr}</h1>
              <h1 className="text-center">{receipt?.saveReceiptMessageEn}</h1>
            </div>

            <div className="flex flex-col  mt-4">
              <h1 className="text-center">** نسخة العميل **</h1>
              <h1 className="text-center">** Customer Copy **</h1>
            </div>

            <div className="text-[.6rem] flex font-normal mt-3 justify-center gap-2">
              <h1>{receipt?.entryMode}</h1>
              <h1>{receipt?.actionCode} </h1>
              <h1> {receipt?.applicationIdentifier}</h1>
              <h1>{receipt?.terminalVerificationResult}</h1>
            </div>

            <div className="text-[.6rem] flex font-normal  justify-center gap-2">
              <h1>{receipt?.transactionStateInformation}</h1>
              <h1>{receipt?.cardholderVerificationResult} </h1>
              <h1> {receipt?.cryptogramInformationData}</h1>
              <h1>{receipt?.applicationCryptogram}</h1>
              <h1>{receipt?.kernelId}</h1>
            </div>
            <div className="text-[.6rem] flex font-normal  justify-center gap-2">
              <h1>{receipt?.paymentAccountReference}</h1>
            </div>

            {/* <h1 className="text-center mt-4">Powered by Neopaas</h1> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptPage;
